import type {
  BuildingEntityBase,
  DocBuildingEntityBase,
  DynamicBuildingEntityBase,
} from "building-types/entities/buildingEntityBase";
import type { BuildingEntityType } from "building-types/entities/buildingEntityTypeTypes";
import type { LabelableEntityBase } from "building-types/entities/labelTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Prop } from "building-types/props/propTypes";
import type { EntityInstanceType } from "building-types/roomLayouts/roomLayoutEligibleDocEntity";
import type { RuleBasedSelectorBuildingEntityType } from "building-types/views/SerializableSheetGeometrySelector";
import { RuleBasedSelectorBuildingEntityTypes } from "building-types/views/SerializableSheetGeometrySelector";
import uid from "common/utils/uid";
import { BuildingGenerationContext } from "./buildingGenerationContext";

export function buildingEntityBaseNew<T extends BuildingEntityType>(
  type: T,
  id: string = uid(),
  docEntityHandle: DocEntityHandle | undefined = undefined,
  entityInstanceType: EntityInstanceType | undefined = undefined,
): BuildingEntityBase<T> {
  return {
    entityType: type,
    id,
    integerId: BuildingGenerationContext.getBuildingEntityInteger(type, id, docEntityHandle),
    docEntityHandle,
    entityInstanceType,
  };
}

export function docBuildingEntityBaseNew<T extends BuildingEntityType>(
  type: T,
  id: string = uid(),
  docEntityHandle: DocEntityHandle,
): DocBuildingEntityBase<T> {
  return {
    entityType: type,
    id,
    integerId: BuildingGenerationContext.getBuildingEntityInteger(type, id, docEntityHandle),
    docEntityHandle,
  };
}

export function dynamicBuildingEntityBaseNew<T extends BuildingEntityType>(
  type: T,
  id: string = uid(),
): DynamicBuildingEntityBase<T> {
  return {
    entityType: type,
    id,
    integerId: BuildingGenerationContext.getBuildingEntityInteger(type, id),
    docEntityHandle: undefined,
  };
}

export type LabelableBuildingEntityType = Extract<BuildingEntityType, "ANNOTATION" | "PROP" | "CEILING">;
export const LabelableBuildingEntityTypes: LabelableBuildingEntityType[] = ["ANNOTATION", "PROP", "CEILING"];

export function buildingEntityIsLabelable(
  entity: BuildingEntityBase<BuildingEntityType>,
): entity is BuildingEntityBase<LabelableBuildingEntityType> & LabelableEntityBase {
  return LabelableBuildingEntityTypes.includes(entity.entityType as LabelableBuildingEntityType);
}

export function buildingEntityIsSelectableByRuleBasedSelector(
  entity: BuildingEntityBase<BuildingEntityType>,
): entity is BuildingEntityBase<RuleBasedSelectorBuildingEntityType> {
  return RuleBasedSelectorBuildingEntityTypes.includes(entity.entityType as RuleBasedSelectorBuildingEntityType);
}

export function isProp(entity: BuildingEntityBase<BuildingEntityType>): entity is Prop {
  return entity.entityType === "PROP";
}
